/**
 * ThreadModals - Rename and Delete confirmation modals for threads
 * Extracted for cleaner component structure
 */

import React, { useRef, useEffect } from "react";
import { View, Text, TextInput, StyleSheet } from "react-native";
import { AxioFormModal, AxioActionModal } from "../../axio-ds/Modal/AxioModal";
import { colors, radii, spacing } from "../../../styles/design-system";

export interface ThreadModalsProps {
  // Rename modal
  renameModalOpen: boolean;
  setRenameModalOpen: (open: boolean) => void;
  renameValue: string;
  setRenameValue: (value: string) => void;
  onRenameSubmit: () => void;
  // Delete modal
  deleteModalOpen: boolean;
  setDeleteModalOpen: (open: boolean) => void;
  selectedThreadTitle?: string;
  onDeleteConfirm: () => void;
}

export const ThreadModals: React.FC<ThreadModalsProps> = ({
  renameModalOpen,
  setRenameModalOpen,
  renameValue,
  setRenameValue,
  onRenameSubmit,
  deleteModalOpen,
  setDeleteModalOpen,
  selectedThreadTitle,
  onDeleteConfirm,
}) => {
  const renameInputRef = useRef<TextInput>(null);

  // Focus rename input when modal opens
  useEffect(() => {
    if (renameModalOpen && renameInputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        renameInputRef.current?.focus();
      }, 100);
    }
  }, [renameModalOpen]);

  return (
    <>
      {/* Rename Thread Modal */}
      <AxioFormModal
        visible={renameModalOpen}
        onRequestClose={() => setRenameModalOpen(false)}
        title="Rename thread"
        subtitle="Enter a new name for this thread."
        primaryActionLabel="Save"
        onPrimaryAction={() => {
          if (renameValue.trim()) {
            onRenameSubmit();
          }
        }}
        secondaryActionLabel="Cancel"
        onSecondaryAction={() => setRenameModalOpen(false)}>
        <TextInput
          ref={renameInputRef}
          style={styles.input}
          value={renameValue}
          onChangeText={setRenameValue}
          placeholder="Thread name..."
          placeholderTextColor={colors.grey[400]}
          onSubmitEditing={onRenameSubmit}
          returnKeyType="done"
        />
      </AxioFormModal>

      {/* Delete Thread Modal */}
      <AxioActionModal
        visible={deleteModalOpen}
        onRequestClose={() => setDeleteModalOpen(false)}
        title="Delete thread"
        primaryActionLabel="Delete"
        onPrimaryAction={onDeleteConfirm}
        secondaryActionLabel="Cancel"
        onSecondaryAction={() => setDeleteModalOpen(false)}>
        <View style={styles.deleteContent}>
          <Text style={styles.deleteText}>
            Are you sure you want to delete "{selectedThreadTitle}"? This action cannot be undone.
          </Text>
        </View>
      </AxioActionModal>
    </>
  );
};

const styles = StyleSheet.create({
  input: {
    width: "100%",
    height: 44,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.md,
    paddingHorizontal: spacing.sm,
    fontSize: 14,
    color: colors.grey[800],
    backgroundColor: colors.white,
  },
  deleteContent: {
    width: "100%",
  },
  deleteText: {
    fontSize: 14,
    color: colors.grey[600],
    textAlign: "center",
    lineHeight: 20,
  },
});
